/**
 * Pattern Engine V1 — deterministic, evidence-based qualitative read.
 *
 * Rules:
 *  - No numeric scores, no LLM, no personality labels.
 *  - A finding normally needs >= 2 supporting signals, preferably from
 *    different rooms. Weak/mixed evidence -> status "insufficient-evidence"
 *    and no forced conclusion.
 *  - Tasks SAMPLE domains; findings speak about choices in the exercises,
 *    never about a whole communication domain.
 */

const GESTURE_BEST = {
  three_priorities: "count",
  move_stronger_position: "move_upward",
  two_paths: "contrast_left_right",
  bring_you_in: "open_invitation",
};

const AUDIENCE_NOTES = ["audience", "think", "feel", "do", "stakes"];
const CONTENT_NOTES = ["evidence", "coverage", "whatToSay", "time"];
const CONNECTION_NOTES = ["audience", "feel", "do"];

const GOAL_KEYWORDS = {
  voice: ["voice", "vocal", "tone", "pitch", "pace", "monotone", "emphasis", "intonation"],
  gesture: ["gesture", "hands", "body language"],
  space: ["space", "stage", "movement", "moving", "presence", "position", "body"],
  structure: ["structure", "story", "flow", "order", "narrative", "organis", "organiz", "sequenc"],
  opening: ["opening", "start", "beginning", "hook", "first minute"],
  message: ["message", "clarity", "clear", "audience", "content", "concise"],
};

const GOAL_LABELS = {
  voice: "Voice", gesture: "Gesture", space: "Space",
  structure: "Structure", opening: "Opening", message: "Message",
};

const sig = (room, detail) => ({ room, detail });
const distinctRooms = (signals) => new Set(signals.map((s) => s.room)).size;

// ---------------------------------------------------------------------------
// Raw feature extraction from the audit state
// ---------------------------------------------------------------------------
function extractFeatures({ answers = {}, reflections = {} }) {
  const forNow = answers.message?.forNow || [];
  const sequence = (answers.structure?.sequence || []).filter(Boolean);
  const emphasis = answers.voice?.emphasis || [];
  const pauses = answers.voice?.pauses || [];
  const picks = answers.gesture?.picks || [];
  const positions = answers.space?.positions || {};
  const path = answers.space?.path || Object.values(positions).filter(Boolean);
  const openingChoice = answers.opening?.selected || null;

  const refl = (key) => reflections?.[key]?.selected || null;
  const finalNotes = (key) => (reflections?.[key]?.notes || "").toLowerCase();

  const meaningMatched = picks.filter((p) => GESTURE_BEST[p.phraseId] === p.selected).length;
  const neutralPicks = picks.filter((p) => p.selected === "neutral").length;
  const distinctGestures = new Set(picks.map((p) => p.selected)).size;
  const distinctCells = new Set(path).size;

  // Stated development goal(s) from final free-text Q2 (+ context from Q3).
  const goalText = finalNotes("reflection_q2");
  const goalAreas = [];
  Object.entries(GOAL_KEYWORDS).forEach(([area, words]) => {
    if (words.some((w) => goalText.includes(w))) goalAreas.push(area);
  });

  return {
    forNow, sequence, emphasis, pauses, picks, positions, path, openingChoice,
    refl, finalNotes, meaningMatched, neutralPicks, distinctGestures, distinctCells,
    goalAreas, goalText,
    unusedCount: 7 - sequence.length,
    audienceNoteCount: forNow.filter((n) => AUDIENCE_NOTES.includes(n)).length,
    contentNoteCount: forNow.filter((n) => CONTENT_NOTES.includes(n)).length,
    connectionNoteCount: forNow.filter((n) => CONNECTION_NOTES.includes(n)).length,
  };
}

// ---------------------------------------------------------------------------
// Pattern-family signal collectors (deterministic)
// ---------------------------------------------------------------------------
function collectFamilies(f) {
  const fam = {};

  fam.audienceOrientation = [];
  if (f.audienceNoteCount >= 2)
    fam.audienceOrientation.push(sig("message", `Prioritised ${f.audienceNoteCount} audience-side notes (think/feel/do/audience/stakes) in "For Now".`));
  if (["audience", "outcome"].includes(f.refl("message")))
    fam.audienceOrientation.push(sig("message", "Said the audience/outcome guided the message choices."));
  if (f.refl("structure") === "audienceInterest")
    fam.audienceOrientation.push(sig("structure", "Ordered the story around keeping the audience engaged."));
  if (f.refl("voice") === "audienceAttention")
    fam.audienceOrientation.push(sig("voice", "Placed emphasis/pauses to help the audience stay with the message."));
  if (f.refl("gesture") === "audience")
    fam.audienceOrientation.push(sig("gesture", "Chose gestures by imagining how the audience receives the message."));
  if (["connection", "interest"].includes(f.refl("opening")))
    fam.audienceOrientation.push(sig("opening", "Values connection/interest first in an opening."));
  if (f.refl("space") === "attention")
    fam.audienceOrientation.push(sig("space", "Chose stage positions around keeping the audience's attention."));

  fam.contentOrientation = [];
  if (f.contentNoteCount >= 2)
    fam.contentOrientation.push(sig("message", `Prioritised ${f.contentNoteCount} content-side notes (evidence/coverage/what-to-say/time) in "For Now".`));
  if (["content", "message", "constraints"].includes(f.refl("message")))
    fam.contentOrientation.push(sig("message", "Said the content/message/constraints guided the choices."));
  if (f.sequence.includes("evidence") && !f.sequence.includes("callToAction"))
    fam.contentOrientation.push(sig("structure", "Built a flow that includes evidence but no call to action."));

  fam.structuralIntentionality = [];
  if (f.sequence.length >= 5)
    fam.structuralIntentionality.push(sig("structure", `Built a deliberate ${f.sequence.length}-step flow.`));
  if (f.sequence.length >= 3 && f.unusedCount >= 1 && f.unusedCount <= 4)
    fam.structuralIntentionality.push(sig("structure", `Left ${f.unusedCount} card(s) out — selective omission.`));
  if (["callToAction", "recommendation"].includes(f.sequence[f.sequence.length - 1]))
    fam.structuralIntentionality.push(sig("structure", "The flow builds toward a recommendation / call to action."));
  if (["persuasion", "logic", "clarity"].includes(f.refl("structure")))
    fam.structuralIntentionality.push(sig("structure", `Described ordering by ${f.refl("structure")}.`));
  if (f.forNow.includes("goal") || f.forNow.includes("coreIdea"))
    fam.structuralIntentionality.push(sig("message", "Kept goal / core idea among the top message priorities."));

  fam.deliveryIntentionality = [];
  if (f.emphasis.length >= 2 && f.pauses.length >= 1)
    fam.deliveryIntentionality.push(sig("voice", `Marked ${f.emphasis.length} emphases and ${f.pauses.length} pause(s) deliberately.`));
  if (f.refl("voice") === "meaning")
    fam.deliveryIntentionality.push(sig("voice", "Said meaning guided the emphasis and pauses."));
  if (f.picks.length >= 4 && f.meaningMatched >= 3)
    fam.deliveryIntentionality.push(sig("gesture", `Matched gesture to meaning in ${f.meaningMatched} of ${f.picks.length} rounds.`));
  if (f.refl("gesture") === "meaning")
    fam.deliveryIntentionality.push(sig("gesture", "Said the meaning to make clearer guided the gesture choices."));
  if (f.distinctCells >= 3)
    fam.deliveryIntentionality.push(sig("space", `Used ${f.distinctCells} different stage positions for different moments.`));
  if (f.refl("space") === "meaning")
    fam.deliveryIntentionality.push(sig("space", "Said the meaning of each moment guided the stage positions."));

  fam.controlledLowVariation = [];
  if (f.picks.length >= 3 && f.neutralPicks >= 2)
    fam.controlledLowVariation.push(sig("gesture", `Chose the neutral stance ${f.neutralPicks} times.`));
  if (f.picks.length >= 3 && f.distinctGestures <= 2)
    fam.controlledLowVariation.push(sig("gesture", "Repeated the same gesture choices across rounds."));
  if (f.path.length >= 3 && f.distinctCells <= 2)
    fam.controlledLowVariation.push(sig("space", `Stayed within ${f.distinctCells} stage position(s) across four moments.`));
  if (f.emphasis.length + f.pauses.length > 0 && f.emphasis.length <= 1 && f.pauses.length <= 1)
    fam.controlledLowVariation.push(sig("voice", "Used minimal contrast in the emphasis/pause exercise."));
  if (f.openingChoice === "welcome")
    fam.controlledLowVariation.push(sig("opening", "Preferred the conventional welcome-and-introduce opening."));
  if (["natural", "instinct"].includes(f.refl("gesture")) || f.refl("space") === "natural")
    fam.controlledLowVariation.push(sig(f.refl("gesture") ? "gesture" : "space", "Described choices as what felt most natural."));

  fam.expressiveHighVariation = [];
  if (f.picks.length >= 4 && f.distinctGestures >= 4 && f.neutralPicks === 0)
    fam.expressiveHighVariation.push(sig("gesture", "Chose a different, non-neutral gesture in every round."));
  if (f.distinctCells >= 4)
    fam.expressiveHighVariation.push(sig("space", "Gave all four moments a different stage position."));
  if (f.emphasis.length >= 3 && f.pauses.length >= 2)
    fam.expressiveHighVariation.push(sig("voice", "Used the full range of emphasis and pause markings."));
  if (["curiosity", "tension", "story", "bold_claim"].includes(f.openingChoice))
    fam.expressiveHighVariation.push(sig("opening", `Chose a differentiated opening (${f.openingChoice}).`));

  fam.audienceConnection = [];
  if (f.connectionNoteCount >= 2)
    fam.audienceConnection.push(sig("message", "Prioritised audience/feel/do among the top message notes."));
  if (f.refl("gesture") === "audience")
    fam.audienceConnection.push(sig("gesture", "Chose gestures around how the audience receives the message."));
  if (["welcome", "relevance", "story"].includes(f.openingChoice))
    fam.audienceConnection.push(sig("opening", `Opened by building the relationship (${f.openingChoice}).`));
  if (f.refl("opening") === "connection")
    fam.audienceConnection.push(sig("opening", "Said connection matters most in an opening."));
  if (f.refl("space") === "attention")
    fam.audienceConnection.push(sig("space", "Positioned around keeping the audience's attention."));

  fam.purposefulMovement = [];
  if (f.distinctCells >= 3)
    fam.purposefulMovement.push(sig("space", `Used ${f.distinctCells} distinct positions across the four moments.`));
  if (f.positions.cta && f.positions.opening && f.positions.cta !== f.positions.opening)
    fam.purposefulMovement.push(sig("space", "Gave the call to action its own position, distinct from the opening."));
  if (f.positions.video && f.positions.tension && f.positions.video !== f.positions.tension)
    fam.purposefulMovement.push(sig("space", "Moved to make room for the projected content."));
  if (["meaning", "stage"].includes(f.refl("space")))
    fam.purposefulMovement.push(sig("space", "Described positioning by the meaning of the moment / what was happening on stage."));

  return fam;
}

const FAMILY_META = {
  audienceOrientation: {
    kind: "strength", title: "Audience-First Orientation",
    text: "Across several exercises, you tended to prioritise what the audience should think, feel or do. Your choices suggest you naturally plan from the audience's side of the room.",
  },
  contentOrientation: {
    kind: "strength", title: "Content Command",
    text: "Your choices suggest you anchor a presentation in its material — evidence, coverage and what you want to say came first in several tasks.",
  },
  structuralIntentionality: {
    kind: "strength", title: "Structural Intentionality",
    text: "You built a deliberate flow and thought about where each idea belongs. Your choices suggest sequencing is something you do on purpose, not by accident.",
  },
  deliveryIntentionality: {
    kind: "strength", title: "Delivery Intentionality",
    text: "In the delivery exercises, your choices showed deliberate use of emphasis, pause, meaning-related gesture or positioning. These tasks sample only slices of delivery, but the intent behind your choices was consistent.",
  },
  audienceConnection: {
    kind: "strength", title: "Connection First",
    text: "Your choices suggest building a relationship with the room matters to you — it appeared in how you opened and what you prioritised.",
  },
  purposefulMovement: {
    kind: "strength", title: "Purposeful Movement", singleRoom: true,
    text: "In the Space exercise, you gave different moments different positions. Your choices suggest movement with a reason. This task sampled intended positions only, not full stage presence.",
  },
  expressiveHighVariation: {
    kind: "strength", title: "Expressive Range",
    text: "Across the delivery exercises you varied gesture, position or vocal marking. Variation is a resource, not a requirement — your choices suggest you are willing to use it.",
  },
  controlledLowVariation: {
    kind: "watch", title: "A Controlled, Low-Variation Style",
    text: "Across several exercises you tended to stay with similar choices — repeated positions or gestures and limited contrast. Control can be a strength; it may simply be worth noticing when more differentiation could serve the message.",
  },
};

// ---------------------------------------------------------------------------
// Finding builders
// ---------------------------------------------------------------------------
const qualifies = (key, signals) => {
  if (!signals || signals.length < 2) return false;
  if (FAMILY_META[key]?.singleRoom) return signals.length >= 3;
  return distinctRooms(signals) >= 2;
};

const insufficient = (title, text) => ({
  title, explanation: text, evidence: [], status: "insufficient-evidence",
});

function pickStrength(fam) {
  const order = [
    "audienceOrientation", "deliveryIntentionality", "structuralIntentionality",
    "audienceConnection", "contentOrientation", "purposefulMovement", "expressiveHighVariation",
  ];
  const candidates = order
    .filter((k) => qualifies(k, fam[k]))
    .sort((a, b) =>
      (distinctRooms(fam[b]) - distinctRooms(fam[a])) ||
      (fam[b].length - fam[a].length) ||
      (order.indexOf(a) - order.indexOf(b)));
  if (!candidates.length)
    return insufficient(
      "No single dominant pattern yet",
      "Your choices varied across the exercises without one orientation appearing repeatedly. That is not a problem — it simply means this Audit did not surface one clear leading strength to name."
    );
  const k = candidates[0];
  return {
    key: k, title: FAMILY_META[k].title, explanation: FAMILY_META[k].text,
    evidence: fam[k], status: "evidence-based",
  };
}

function pickWatch(fam, f, strengthKey) {
  // Meta-family: Embodiment Opportunity
  const structIntent = qualifies("structuralIntentionality", fam.structuralIntentionality) ||
    fam.structuralIntentionality.length >= 2;
  const lowVar = fam.controlledLowVariation;
  if (structIntent && lowVar.length >= 2 && distinctRooms(lowVar) >= 2) {
    return {
      key: "embodimentOpportunity", title: "Thinking Ahead of the Body",
      explanation: "Your Message and Structure choices showed clear intentionality, while the delivery exercises showed comparatively less differentiation. These tasks sample only fragments of delivery — the contrast is simply worth exploring, not a verdict on how you actually deliver.",
      evidence: [...fam.structuralIntentionality.slice(0, 2), ...lowVar.slice(0, 3)],
      status: "evidence-based",
    };
  }
  // Meta-family: Structure Opportunity
  const delivIntent = fam.deliveryIntentionality;
  if (delivIntent.length >= 2 && distinctRooms(delivIntent) >= 2 && f.sequence.length > 0 && f.sequence.length <= 3) {
    return {
      key: "structureOpportunity", title: "Delivery Ahead of Structure",
      explanation: "Your delivery choices showed variation and intent, while the Structure exercise showed comparatively limited sequencing. The order of ideas may be where deliberate attention pays off fastest.",
      evidence: [...delivIntent.slice(0, 3), sig("structure", `Placed ${f.sequence.length} of 7 cards on the timeline.`)],
      status: "evidence-based",
    };
  }
  if (strengthKey !== "controlledLowVariation" && qualifies("controlledLowVariation", lowVar)) {
    return {
      key: "controlledLowVariation", title: FAMILY_META.controlledLowVariation.title,
      explanation: FAMILY_META.controlledLowVariation.text,
      evidence: lowVar, status: "evidence-based",
    };
  }
  // Orientation tension: content-first pattern when audience side is lighter.
  if (strengthKey !== "contentOrientation" && qualifies("contentOrientation", fam.contentOrientation) &&
      f.audienceNoteCount <= 1) {
    return {
      key: "contentFirstPattern", title: "A Content-First Pattern",
      explanation: "Several choices leaned toward information and coverage more than audience effect. Content command is valuable — the question worth exploring is whether the audience's think / feel / do gets equal attention.",
      evidence: fam.contentOrientation, status: "evidence-based",
    };
  }
  // Otherwise: second-ranked qualifying family different from the strength.
  const rest = Object.keys(FAMILY_META)
    .filter((k) => k !== strengthKey && qualifies(k, fam[k]))
    .sort((a, b) => (distinctRooms(fam[b]) - distinctRooms(fam[a])) || (fam[b].length - fam[a].length));
  if (rest.length) {
    const k = rest[0];
    return {
      key: k, title: FAMILY_META[k].title, explanation: FAMILY_META[k].text,
      evidence: fam[k], status: "evidence-based",
    };
  }
  return insufficient(
    "Nothing forcing itself forward",
    "No second pattern showed up with enough supporting evidence across rooms to be worth naming. The full report will revisit this with everything you shared."
  );
}

const FOCUS_META = {
  space: {
    title: "Space & Purposeful Movement",
    base: "Based on the patterns visible in this Audit, giving key moments a deliberate stage position may be worth prioritising first — it could give you a faster overall gain. This exercise sampled intended positions only, not full stage presence, and this does not mean other areas are unimportant.",
  },
  structure: {
    title: "Structure & Sequencing",
    base: "Based on the patterns visible in this Audit, the order and development of your ideas may be the most useful first experiment. Testing whether every beat moves the audience toward the desired action could give you a faster overall gain. This does not mean other areas are unimportant.",
  },
  message: {
    title: "Message Priorities",
    base: "Based on the patterns visible in this Audit, planning from the audience's side — what they should think, feel and do — may be worth prioritising first. This does not mean other areas are unimportant.",
  },
  deliveryContrast: {
    title: "Delivery Contrast",
    base: "Based on the patterns visible in this Audit, adding one deliberate contrast — an emphasis, a pause, a meaningful gesture, a purposeful move — may be the most useful first experiment. These exercises sample only slices of delivery; this is a starting point, not a verdict.",
  },
};

const EXERCISES = {
  deliveryContrast: "Take a 60-second section of your next presentation. Mark one emphasis, one pause, one meaningful gesture and one purposeful movement.",
  space: "Choose three moments — opening, key idea and close. Give each a position. Move during the transition and land before speaking.",
  message: "Before building slides, write three lines: What should they Think? What should they Feel? What should they Do?",
  structure: "Reduce the presentation to 5–7 beats and test whether every beat moves the audience toward the desired action.",
};

const GOAL_TO_FOCUS = {
  voice: "deliveryContrast", gesture: "deliveryContrast", space: "space",
  structure: "structure", opening: "message", message: "message",
};

function pickFocus(fam, f, strength, watch) {
  const evidence = [];
  let area = null;

  if (watch.key === "embodimentOpportunity") { area = "space"; evidence.push(...watch.evidence); }
  else if (watch.key === "structureOpportunity") { area = "structure"; evidence.push(...watch.evidence); }
  else if (watch.key === "controlledLowVariation") { area = "deliveryContrast"; evidence.push(...watch.evidence); }
  else {
    // Weakest observed differentiation, not already the strength.
    if (f.path.length >= 3 && f.distinctCells <= 2 && strength.key !== "purposefulMovement") {
      area = "space"; evidence.push(sig("space", `Used ${f.distinctCells} position(s) across four moments.`));
    } else if (f.sequence.length > 0 && f.sequence.length <= 4 && strength.key !== "structuralIntentionality") {
      area = "structure"; evidence.push(sig("structure", `Placed ${f.sequence.length} of 7 cards on the timeline.`));
    } else if (f.forNow.length >= 5 && f.audienceNoteCount <= 1 &&
               !["audienceOrientation", "audienceConnection"].includes(strength.key)) {
      area = "message"; evidence.push(sig("message", "Audience-side notes were largely left for later."));
    } else if (f.emphasis.length + f.pauses.length > 0 && (f.emphasis.length <= 1 || f.pauses.length === 0) &&
               strength.key !== "deliveryIntentionality") {
      area = "deliveryContrast"; evidence.push(sig("voice", "Minimal contrast in the emphasis/pause exercise."));
    }
  }

  // Fold in the user's stated goal.
  const goalFocus = f.goalAreas.length ? GOAL_TO_FOCUS[f.goalAreas[0]] : null;
  if (!area) {
    // No observed gap — follow the stated goal if there is one.
    if (goalFocus) { area = goalFocus; evidence.push(sig("final-reflection", "Your stated development goal.")); }
    else return insufficient(
      "No single first focus yet",
      "The Audit did not surface one area with clearly less differentiation than the rest, and no development goal was described. The full report will propose a starting point using everything you shared."
    );
  }

  let explanation = FOCUS_META[area].base;
  if (goalFocus && f.goalAreas.length) {
    const label = GOAL_LABELS[f.goalAreas[0]];
    if (goalFocus === area) {
      explanation += ` This also matches the ${label} goal you described — a natural place to start.`;
    } else {
      explanation += ` Your stated ${label} goal remains fully valid — this focus is simply where the Audit patterns suggest a faster first gain, alongside it.`;
      evidence.push(sig("final-reflection", `You described wanting to develop ${label}.`));
    }
  }

  return {
    key: area, title: FOCUS_META[area].title, explanation,
    evidence, status: evidence.length ? "evidence-based" : "insufficient-evidence",
  };
}

function pickReflectionInsight(fam, f) {
  // Useful contrast: stated goal vs observed intentionality in that same sampled task.
  if (f.goalAreas.includes("voice") && f.emphasis.length >= 2 && f.pauses.length >= 1) {
    return {
      title: "Your Voice goal, seen against your choices",
      explanation: "You said Voice is an area you want to develop. In this Audit you nevertheless made deliberate choices around emphasis and pause. That does not assess your wider vocal delivery — it simply suggests you already bring some intentionality to this part of Voice.",
      evidence: [sig("final-reflection", "Stated Voice as a development goal."), sig("voice", `Marked ${f.emphasis.length} emphases and ${f.pauses.length} pause(s).`)],
      status: "evidence-based",
    };
  }
  if ((f.goalAreas.includes("space") || f.goalAreas.includes("gesture")) && f.distinctCells >= 3) {
    return {
      title: "Your delivery goal, seen against your choices",
      explanation: "You described wanting to develop your physical delivery. In the Space exercise you already gave different moments different positions. That does not assess real stage movement — it suggests the intention is already there to build on.",
      evidence: [sig("final-reflection", "Stated a physical-delivery development goal."), sig("space", `Used ${f.distinctCells} distinct positions.`)],
      status: "evidence-based",
    };
  }
  if (f.goalAreas.includes("structure") && f.sequence.length >= 5) {
    return {
      title: "Your Structure goal, seen against your choices",
      explanation: "You said Structure is where you want to grow. In the Structure exercise you already built a multi-step flow deliberately. The exercise samples sequencing only — but it suggests a base of intentionality to develop from.",
      evidence: [sig("final-reflection", "Stated Structure as a development goal."), sig("structure", `Built a ${f.sequence.length}-step flow.`)],
      status: "evidence-based",
    };
  }
  // Alignment: audience-flavoured reflections + audience-flavoured behaviour.
  const audienceRefl = ["message", "structure", "voice", "gesture", "space", "opening"]
    .filter((r) => (fam.audienceOrientation.concat(fam.audienceConnection)).some((s) => s.room === r && /audience|connection|engag|attention/i.test(s.detail)));
  if (audienceRefl.length >= 2 && (qualifies("audienceOrientation", fam.audienceOrientation) || qualifies("audienceConnection", fam.audienceConnection))) {
    return {
      title: "What you said matches what you chose",
      explanation: "You described the audience as central to how you decide — and that also appeared across several of your Message, Gesture and Opening choices. Stated intent and sampled behaviour point the same way here.",
      evidence: fam.audienceOrientation.concat(fam.audienceConnection).slice(0, 4),
      status: "evidence-based",
    };
  }
  const meaningRefl = [f.refl("voice"), f.refl("gesture"), f.refl("space")].filter((r) => r === "meaning").length;
  if (meaningRefl >= 2 && qualifies("deliveryIntentionality", fam.deliveryIntentionality)) {
    return {
      title: "Meaning as your compass",
      explanation: "In several exercises you described meaning as what guided you — and your choices in those same tasks were consistently meaning-related. What you say about your process matches what you did.",
      evidence: fam.deliveryIntentionality.slice(0, 4),
      status: "evidence-based",
    };
  }
  return insufficient(
    "Recorded for the full report",
    "Your written reflections were recorded and will be read alongside your observed choices in the full personalised report. This preview only names comparisons with enough supporting evidence."
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function buildPatternRead(state) {
  const f = extractFeatures(state || {});
  const fam = collectFamilies(f);

  const naturalStrength = pickStrength(fam);
  const patternToWatch = pickWatch(fam, f, naturalStrength.key);
  const firstDevelopmentFocus = pickFocus(fam, f, naturalStrength, patternToWatch);
  const reflectionInsight = pickReflectionInsight(fam, f);

  const exerciseKey = firstDevelopmentFocus.key && EXERCISES[firstDevelopmentFocus.key]
    ? firstDevelopmentFocus.key : "deliveryContrast";
  const recommendedExercise = firstDevelopmentFocus.status === "evidence-based"
    ? { focusArea: exerciseKey, title: FOCUS_META[exerciseKey].title, instruction: EXERCISES[exerciseKey] }
    : { focusArea: "deliveryContrast", title: FOCUS_META.deliveryContrast.title, instruction: EXERCISES.deliveryContrast };

  return {
    naturalStrength,
    patternToWatch,
    firstDevelopmentFocus,
    reflectionInsight,
    recommendedExercise,
    evidence: [
      ...naturalStrength.evidence, ...patternToWatch.evidence,
      ...firstDevelopmentFocus.evidence, ...reflectionInsight.evidence,
    ],
    meta: {
      version: "v1-deterministic",
      generatedAt: new Date().toISOString(),
      familySignals: Object.fromEntries(Object.entries(fam).map(([k, v]) => [k, v.length])),
      statedGoalAreas: f.goalAreas,
    },
  };
}

export default buildPatternRead;

// ---------------------------------------------------------------------------
// Full report payload — ordered sections for the later personalised report.
// ---------------------------------------------------------------------------
const SCOPE_NOTE =
  "The Audit samples selected aspects of each communication area. A deliberate choice in one exercise never means that area needs no further development.";

export function buildReportPayload({ answers = {}, tags = [], reflections = {} }, patternRead, contact, commercialContext = {}) {
  const q = (k) => reflections?.[k]?.notes || "";
  const goalText = q("reflection_q2");
  const strengthOk = patternRead.naturalStrength.status === "evidence-based";
  const snapshot = strengthOk
    ? `Across the Audit, your choices suggest "${patternRead.naturalStrength.title}" as a recurring orientation, with "${patternRead.patternToWatch.title}" worth exploring and "${patternRead.firstDevelopmentFocus.title}" as a possible first focus.`
    : "Your choices varied across the exercises without one dominant orientation appearing repeatedly. The sections below hold only what the Audit could support with evidence.";

  return {
    contact,
    submittedAt: new Date().toISOString(),
    audit: { answers, tags, reflections },
    patternRead,
    // Room 7's five new required questions + the results-form hidden GA
    // field — kept as their OWN top-level section, entirely separate from
    // `audit`/`patternRead`/`report` above. User-provided commercial
    // context only: never read by the Pattern Engine, never affects
    // report content/exercise mapping/recommendation, never implies
    // marketing consent. Mirrors the exact field names the OP16/n8n CRM
    // payload uses server-side (see crm_sync_adapter.py).
    commercialContext: {
      coaching_support_interest: commercialContext.coachingSupportInterest ?? null,
      next_presentation_timing: commercialContext.nextPresentationTiming ?? null,
      presentation_context: commercialContext.presentationContext ?? null,
      presentation_context_other: commercialContext.presentationContextOther ?? null,
      support_priorities: commercialContext.supportPriorities ?? null,
      support_priorities_other: commercialContext.supportPrioritiesOther ?? null,
      funding_source: commercialContext.fundingSource ?? null,
      ga_client_id: commercialContext.gaClientId ?? null,
    },
    report: {
      sections: [
        { id: "auditSnapshot", heading: "Your Audit Snapshot", body: snapshot },
        { id: "naturalStrength", heading: "Your Natural Strength",
          body: patternRead.naturalStrength.explanation, evidence: patternRead.naturalStrength.evidence },
        { id: "patternWorthExploring", heading: "A Pattern Worth Exploring",
          body: patternRead.patternToWatch.explanation, evidence: patternRead.patternToWatch.evidence },
        { id: "whatYouNoticed", heading: "What You Noticed About Yourself",
          body: patternRead.reflectionInsight.explanation, userNotes: q("reflection_q1"),
          evidence: patternRead.reflectionInsight.evidence },
        { id: "whatYouWantToDevelop", heading: "What You Want to Develop",
          body: goalText
            ? `You described wanting to develop: \u201c${goalText}\u201d. That goal remains fully valid. ${SCOPE_NOTE}`
            : `You did not name a specific development goal. ${SCOPE_NOTE}`,
          userNotes: goalText },
        { id: "firstDevelopmentFocus", heading: "Your First Development Focus",
          body: patternRead.firstDevelopmentFocus.explanation, evidence: patternRead.firstDevelopmentFocus.evidence },
        { id: "recommendedExercise", heading: "Your Recommended Exercise",
          body: patternRead.recommendedExercise.instruction, focusArea: patternRead.recommendedExercise.focusArea },
        { id: "whatToTryNext", heading: "What to Try Next",
          body: "In your next real presentation, apply the exercise above to one short section only — then notice what changed for you and for the audience." },
      ],
    },
  };
}
